import {
  Avatar,
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import {
  CaretLeft,
  CaretRight,
  ChartLineUp,
  ChatCircleText,
  Gear,
  House,
  Question,
  SquaresFour,
  Stack as StackIcon,
  Translate,
  UserCircle,
  UserGear,
  Users,
  UsersThree
} from "@phosphor-icons/react";
import SidebarPhoto from "assets/images/Photo.png";
import { LanguageSelector } from "components/UI/LanguageSelector";
import { IMAGE_URL } from "config";
import { useChurchStore } from "features/churches";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAclStore } from "stores/aclStore";
import { useSidebarStore } from "stores/sidebarStore";

export function MainSidebar({ forceOpen = false }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const role = useAclStore(state => state.role);
  const { t } = useTranslation();
  const isOpenState = useSidebarStore(state => state.isOpen);
  const setIsOpen = useSidebarStore(state => state.setIsOpen);
  // In the mobile drawer the sidebar is always fully expanded to match the
  // desktop sidebar; the collapse toggle is hidden (see below).
  const isOpen = forceOpen || isOpenState;

  const handleDrawerToggle = () => {
    setIsOpen(!isOpen);
  };

  const sidebarItems = [
    {
      name: t("sidebar.home"),
      icon: <House size={24} />,
      path: "/quickboard"
    },
    {
      name: t("sidebar.dashboard"),
      icon: <SquaresFour size={24} />,
      path: "/"
    },
    {
      name: t("sidebar.members"),
      icon: <Users size={24} />,
      path: "/members",
      // Kids are managed under the Members section but live on their own route.
      matchPaths: ["/kids"]
    },
    {
      name: t("sidebar.teams"),
      icon: <UsersThree size={24} />,
      path: "/teams"
    },
    {
      name: t("sidebar.message"),
      icon: <ChatCircleText size={24} />,
      path: "/messages"
    },
    {
      name: t("sidebar.finance"),
      icon: <ChartLineUp size={24} />,
      path: "/finances"
    },
    {
      name: t("HR.HR.HR"),
      icon: <UserGear size={24} />,
      path: "/hr"
    },
    {
      name: t("Asset.Asset.Asset"),
      icon: <StackIcon size={24} />,
      path: "/assets"
    },
    {
      name: t("sidebar.systemAdmins"),
      icon: <UserCircle size={24} />,
      path: "/users",
      // Role management pages live under /roles, outside the /users tree.
      matchPaths: ["/roles"],
      role: "OWNER"
    },
    {
      name: t("sidebar.settings"),
      icon: <Gear size={24} />,
      path: "/settings"
    },
    // Help Center lives in the header on desktop; surface it in the mobile
    // drawer where the header hides it.
    ...(forceOpen
      ? [
          {
            name: t("Help.Help.HelpCenter"),
            icon: <Question size={24} />,
            path: "/help-center"
          }
        ]
      : [])
  ];

  // Highlight a menu item when the current route matches its path or any of its
  // related paths. Prefix matching keeps the parent item active on nested routes
  // (e.g. "/members" stays active on "/members/add"); "/" matches exactly so the
  // Dashboard isn't always active.
  const isItemActive = item => {
    if (item.path === "/") {
      return pathname === "/";
    }

    const paths = [item.path, ...(item.matchPaths ?? [])];

    return paths.some(
      path => pathname === path || pathname.startsWith(`${path}/`)
    );
  };

  return (
    <Drawer
      variant="permanent"
      open={isOpen}
      sx={{
        width: isOpen ? 240 : 70,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: isOpen ? 240 : 70,
          boxSizing: "border-box",
          borderRight: forceOpen ? "none" : undefined,
          transition: theme =>
            theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen
            })
        }
      }}
    >
      <Box
        sx={{
          backgroundImage: `url(${SidebarPhoto})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
          py: {
            xs: 4,
            md: 6
          },
          display: "flex",
          flexDirection: "column",
          color: "white",
          height: "100%",
          alignItems: "inherit"
        }}
      >
        <Stack alignItems="center" spacing={1.5} sx={{ mx: 0 }}>
          {!forceOpen && (
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                color: "white",
                alignSelf: isOpen ? "flex-end" : "center"
              }}
            >
              {isOpen ? (
                <CaretLeft size={24} color="#ffffff" />
              ) : (
                <CaretRight size={24} color="#ffffff" />
              )}
            </IconButton>
          )}
          {isOpen && (
            <>
              <Stack direction="row" justifyContent="center">
                <Avatar
                  alt={selectedChurch?.name}
                  src={`${IMAGE_URL}/${selectedChurch?.image_url}`}
                  sx={{
                    width: 140,
                    height: 140,
                    border: "6px solid white"
                  }}
                />
              </Stack>
              <Typography variant="h6" textAlign="center" px={1}>
                {selectedChurch?.name}
              </Typography>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "white",
                  color: "primary.main",
                  "&:hover": {
                    backgroundColor: "white",
                    color: "primary.main"
                  }
                }}
                onClick={() => navigate(`/churches/${selectedChurch?.id}`)}
              >
                {t("sidebar.viewProfile")}
              </Button>
            </>
          )}
          {!isOpen && (
            <Avatar
              alt={selectedChurch?.name}
              src={`${IMAGE_URL}/${selectedChurch?.image_url}`}
              sx={{
                width: 50,
                height: 50,
                border: "3px solid white"
              }}
            />
          )}
        </Stack>
        <List
          sx={{
            my: "auto",
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              display: "none"
            }
          }}
        >
          {sidebarItems.map(item => {
            if (item.role && item.role !== role) {
              return null;
            }

            const active = isItemActive(item);

            return (
              <ListItem key={item.name} disablePadding>
                <Tooltip title={isOpen ? "" : item.name} placement="right">
                  <ListItemButton
                    sx={{
                      pl: 3,
                      "&:hover": {
                        backgroundColor: "white",
                        color: "primary.main"
                      },
                      "&:hover > .MuiListItemIcon-root": {
                        color: "primary.main"
                      },
                      "&.active": {
                        backgroundColor: "white",
                        color: "primary.main",
                        ".MuiListItemIcon-root": {
                          color: "primary.main"
                        }
                      }
                    }}
                    component={Link}
                    to={item.path}
                    className={active ? "active" : undefined}
                    aria-current={active ? "page" : undefined}
                  >
                    <ListItemIcon sx={{ color: "white", minWidth: "34px" }}>
                      {item.icon}
                    </ListItemIcon>
                    {isOpen && (
                      <ListItemText
                        primary={item.name}
                        sx={{
                          "& .MuiTypography-root": { fontSize: "0.8675rem" }
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          })}
        </List>
        {forceOpen && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              pl: 3,
              pb: 2,
              color: "white",
              "& select": {
                color: "white",
                fontWeight: 700,
                fontSize: "0.8675rem"
              }
            }}
          >
            <Translate size={24} color="#ffffff" />
            <LanguageSelector />
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}
